import { AppGlueCoreInit } from "../../../../../AppGlueCoreInit";
import { DataDefinition } from "../../../../../rule-engine-core/Common/Data/DataDefinition";
import { BooleanDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/BooleanDataDefinition";
import { NumberDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import { StringDataDefinition } from "../../../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { StatementRuleConfiguration } from "../../../../../rule-engine-core/Rule/Statement/StatementRuleConfiguration";
import type { EnrichedPart } from "../../../domain/types";
import type { DemoOutputQueue, DemoRuleStatus } from "../../../domain/ruleTypes";
import { getOutcomeRouting, type RuleOutcomeKey, type RuleOutcomeRouting } from "../../../rules/ruleLogicDefinitions";
import { PART_CATEGORIES } from "../../../data/generatorConfig";
import {
    runStatementRuleBoolean,
} from "../../executableFamilies/statementRule/StatementRuleIntegrationAdapter";
import type { RuleIntegrationEvaluationContext } from "../../ruleIntegrationTypes";
import {
    buildRequiredFieldsExpression,
    createStatementRuleSimplifiedLogicBridge,
    getRequiredFieldsCondition,
    type RequiredFieldsBridgeConfig,
} from "../../statementRuleSimplifiedLogic";
import { requireRulePolicyBinding } from "../rulePolicyBindingRegistry";
import type { StatementRuleIntegrationPolicy } from "../statementRulePolicyTypes";

export const DQ001_RULE_ID = "DQ-001";
export const DQ001_APPGLUE_EXECUTABLE_TYPE = "StatementRuleConfiguration";
export const DQ001_OUTPUT_QUEUE: DemoOutputQueue = "quarantine_records";
export const DQ001_REQUIRED_FIELDS = ["package_type", "pin_count", "voltage_range", "rohs_status"] as const;

export type Dq001RequiredField = (typeof DQ001_REQUIRED_FIELDS)[number];

export type Dq001MandatoryAttributeEvidence = {
    status: DemoRuleStatus;
    applicableCategory: boolean;
    configuredCategory: string;
    requiredFields: Dq001RequiredField[];
    missingFields: Dq001RequiredField[];
    appGlueRuntimeError?: string;
    unsupportedNativeExpression?: boolean;
    outputQueue?: DemoOutputQueue;
};

const DEFAULT_DQ001_OUTCOME_ROUTING: Record<RuleOutcomeKey, RuleOutcomeRouting> = {
    pass: {
        id: "dq001-pass-routing",
        when: "pass",
        status: "pass",
        label: "allow complete rows",
        reasonCode: "COMPLETE",
        explanation: "Required attributes are complete.",
    },
    fail: {
        id: "dq001-fail-routing",
        when: "fail",
        status: "rejected",
        label: "quarantine incomplete rows",
        outputQueue: DQ001_OUTPUT_QUEUE,
        reasonCode: "MISSING_REQUIRED_ATTRIBUTES",
        explanation: "A matching row is missing required publish fields.",
    },
    error: {
        id: "dq001-error-routing",
        when: "error",
        status: "rejected",
        label: "hold incomplete expression",
        outputQueue: "pending_review",
        reasonCode: "APPGLUE_EXPRESSION_INCOMPLETE",
        explanation:
            "The AppGlue expression is incomplete, so validation preview is holding rows until the expression is completed.",
    },
};

const getDq001OutcomeRouting = (
    context: RuleIntegrationEvaluationContext | undefined,
    when: RuleOutcomeKey,
): RuleOutcomeRouting => getOutcomeRouting(context?.definition, when) ?? DEFAULT_DQ001_OUTCOME_ROUTING[when];

const FIELD_LABELS: Record<Dq001RequiredField, string> = {
    package_type: "Package",
    pin_count: "Pin count",
    voltage_range: "Voltage range",
    rohs_status: "RoHS",
};

const DQ001_CATEGORY_FIELD = "category";
const DQ001_CATEGORY_CONDITION_ID = "dq001-category-condition";
const DQ001_REQUIRED_FIELDS_ID = "dq001-required-fields";
const DQ001_SIMPLIFIED_LOGIC_CONFIG: RequiredFieldsBridgeConfig = {
    mode: "required_fields_for_condition",
    conditionId: DQ001_CATEGORY_CONDITION_ID,
    requirementId: DQ001_REQUIRED_FIELDS_ID,
    conditionField: DQ001_CATEGORY_FIELD,
    conditionValueOptions: PART_CATEGORIES.map((category) => ({
        value: category,
        label: category,
    })),
    conditionValueInputLabel: "category value",
    requiredFieldOptions: DQ001_REQUIRED_FIELDS.map((field) => ({
        value: field,
        label: field,
    })),
    requiredFieldInputLabel: "required fields",
    defaultConditionValue: "Semiconductor",
    defaultRequiredFields: [...DQ001_REQUIRED_FIELDS],
    actionFor: (state, context) =>
        getDq001OutcomeRouting(context, state.requiredFields.length > 0 ? "fail" : "pass").label,
};
const DQ001_SIMPLIFIED_LOGIC_BRIDGE = createStatementRuleSimplifiedLogicBridge(DQ001_SIMPLIFIED_LOGIC_CONFIG);

const createDq001InputDefinition = (): DataDefinition => {
    const inputData = new DataDefinition();
    inputData.fields.push(new StringDataDefinition("category"));
    inputData.fields.push(new StringDataDefinition("package_type"));
    inputData.fields.push(new NumberDataDefinition("pin_count"));
    inputData.fields.push(new StringDataDefinition("voltage_range"));
    inputData.fields.push(new StringDataDefinition("rohs_status"));
    return inputData;
};

export const formatDq001RequiredField = (field: Dq001RequiredField): string => FIELD_LABELS[field];

const voltageRange = (part: EnrichedPart): string | null =>
    part.voltageMin == null || part.voltageMax == null ? null : `${part.voltageMin}-${part.voltageMax}V`;

const formatNullable = (value: string | number | boolean | null | undefined): string => {
    if (value === null || value === undefined || value === "") return "missing";
    return String(value);
};

export const getDq001RequiredFieldValue = (part: EnrichedPart, field: Dq001RequiredField): string | number | null => {
    if (field === "package_type") return part.packageType;
    if (field === "pin_count") return part.pinCount;
    if (field === "voltage_range") return voltageRange(part);
    return part.rohsStatus;
};

export const getDq001MissingRequiredFields = (part: EnrichedPart): Dq001RequiredField[] =>
    DQ001_REQUIRED_FIELDS.filter((field) => {
        const value = getDq001RequiredFieldValue(part, field);
        return value === null || value === "";
    });

export const buildDq001ExecutionData = (part: EnrichedPart): ExecutionData =>
    new ExecutionData({
        category: part.category,
        package_type: part.packageType,
        pin_count: part.pinCount,
        voltage_range: voltageRange(part),
        rohs_status: part.rohsStatus,
    });

export const createDq001StatementRuleConfiguration = (): StatementRuleConfiguration => {
    AppGlueCoreInit.init();

    const config = new StatementRuleConfiguration();
    config.title = "DQ-001 Mandatory attribute completeness";
    config.inputData = createDq001InputDefinition();
    config.executionResult = new BooleanDataDefinition();

    config.expression.expression = buildRequiredFieldsExpression(DQ001_SIMPLIFIED_LOGIC_CONFIG, {
        conditionValue: "Semiconductor",
        requiredFields: [...DQ001_REQUIRED_FIELDS],
    });

    return config;
};

const getConfiguredDq001Condition = (config: StatementRuleConfiguration) => {
    const condition = getRequiredFieldsCondition(config, DQ001_SIMPLIFIED_LOGIC_CONFIG);
    if (!condition) return null;

    return {
        category: condition.conditionValue,
        requiredFields: condition.requiredFields as Dq001RequiredField[],
    };
};

const evaluateDq001Part = (
    config: StatementRuleConfiguration,
    part: EnrichedPart,
    context?: RuleIntegrationEvaluationContext,
) => {
    const { appGluePassed, appGlueRuntimeError } = runStatementRuleBoolean(config, buildDq001ExecutionData(part));
    const configuredCondition = getConfiguredDq001Condition(config);
    if (!appGlueRuntimeError && !configuredCondition) {
        const routing = getDq001OutcomeRouting(context, appGluePassed ? "pass" : "error");

        return {
            appGluePassed,
            routing,
            evidence: {
                status: routing.status,
                applicableCategory: true,
                configuredCategory: "native AppGlue expression",
                requiredFields: [],
                missingFields: [],
                unsupportedNativeExpression: true,
                outputQueue: routing.outputQueue,
            } satisfies Dq001MandatoryAttributeEvidence,
        };
    }

    const characterizedCondition = configuredCondition ?? {
        category: "Semiconductor",
        requiredFields: [...DQ001_REQUIRED_FIELDS],
    };
    const applicableCategory = part.category === characterizedCondition.category;
    const missingFields = appGlueRuntimeError
        ? []
        : characterizedCondition.requiredFields.filter((field) => {
              const value = getDq001RequiredFieldValue(part, field);
              return value === null || value === "";
          });
    const status: DemoRuleStatus = appGlueRuntimeError
        ? "rejected"
        : !applicableCategory
          ? "skipped"
          : appGluePassed
            ? "pass"
            : "rejected";

    const routing = getDq001OutcomeRouting(
        context,
        appGlueRuntimeError ? "error" : status === "rejected" ? "fail" : "pass",
    );

    return {
        appGluePassed,
        routing,
        evidence: {
            status: status === "skipped" ? status : routing.status,
            applicableCategory,
            configuredCategory: characterizedCondition.category,
            requiredFields: characterizedCondition.requiredFields,
            missingFields,
            appGlueRuntimeError,
            outputQueue: status === "skipped" ? undefined : routing.outputQueue,
        } satisfies Dq001MandatoryAttributeEvidence,
    };
};

export const DQ001_STATEMENT_RULE_POLICY: StatementRuleIntegrationPolicy<EnrichedPart> = {
    ruleId: DQ001_RULE_ID,
    policyBinding: requireRulePolicyBinding(DQ001_RULE_ID),
    createConfig: createDq001StatementRuleConfiguration,
    evaluator: {
        evaluate: (config, part, context) => {
            const { appGluePassed, evidence, routing } = evaluateDq001Part(config, part, context);

            return {
                passed: appGluePassed,
                status: evidence.status,
                reasonCode: evidence.appGlueRuntimeError
                    ? routing.reasonCode
                    : evidence.unsupportedNativeExpression
                        ? evidence.status === "pass"
                            ? "APPGLUE_NATIVE_EXPRESSION_PASSED"
                            : "APPGLUE_NATIVE_EXPRESSION_REJECTED"
                        : evidence.status === "skipped"
                            ? "NOT_APPLICABLE_CATEGORY"
                            : evidence.status === "pass"
                              ? routing.reasonCode
                              : routing.reasonCode,
                explanation: evidence.appGlueRuntimeError
                    ? routing.explanation
                    : evidence.unsupportedNativeExpression
                        ? "The native AppGlue expression evaluated this row, but its shape is outside the simplified required-field evidence model. Use the native editor for full logic."
                        : evidence.status === "skipped"
                            ? `Mandatory attribute rule applies to ${evidence.configuredCategory} rows only.`
                            : evidence.status === "pass"
                              ? routing.explanation
                              : `${routing.explanation} Missing fields: ${evidence.missingFields
                                    .map(formatDq001RequiredField)
                                    .join(", ")}.`,
                outputQueue: evidence.outputQueue,
                comparisons: evidence.appGlueRuntimeError
                    ? [
                          {
                              label: "AppGlue expression",
                              expected: "complete executable expression",
                              actual: evidence.appGlueRuntimeError,
                              ok: false,
                          },
                      ]
                    : evidence.unsupportedNativeExpression
                        ? [
                              {
                                  label: "AppGlue expression",
                                  expected: "row passes native expression",
                                  actual: appGluePassed ? "passed" : "rejected",
                                  ok: appGluePassed,
                              },
                          ]
                    : evidence.status === "skipped"
                        ? [{ label: "Category", expected: evidence.configuredCategory, actual: part.category, ok: false }]
                        : evidence.requiredFields.map((field) => ({
                              label: formatDq001RequiredField(field),
                              expected: "present",
                              actual: formatNullable(getDq001RequiredFieldValue(part, field)),
                              ok: !evidence.missingFields.includes(field),
                          })),
                runtimeEvidence: {
                    runtime: "appglue_runtime",
                    executableType: DQ001_APPGLUE_EXECUTABLE_TYPE,
                },
                evidence,
            };
        },
    },
    summaryPresenter: DQ001_SIMPLIFIED_LOGIC_BRIDGE.summaryPresenter,
    simplifiedLogicEditor: DQ001_SIMPLIFIED_LOGIC_BRIDGE.simplifiedLogicEditor,
    runtimeSamples: {
        get passingRow() {
            return DQ001_RUNTIME_SAMPLES.passingRow;
        },
        get routedRow() {
            return DQ001_RUNTIME_SAMPLES.routedRow;
        },
    },
};

const samplePart = (overrides: Partial<EnrichedPart> = {}): EnrichedPart => ({
    partId: "PART-DQ001-SAMPLE",
    mpn: "TS-0001SEM",
    manufacturerId: "MFR-001",
    manufacturerName: "Texas Instruments",
    category: "Semiconductor",
    taxonomyPath: "Semiconductor > Logic",
    packageType: "QFN",
    pinCount: 32,
    voltageMin: 1.8,
    voltageMax: 3.3,
    capacitancePf: null,
    resistanceOhms: null,
    tolerancePct: null,
    rohsStatus: "Compliant",
    reachStatus: "Compliant",
    lifecycleStatus: "Active",
    priorLifecycleStatus: "Active",
    confidenceScore: 96,
    source: "manufacturer_direct",
    recommendedReplacementMpn: null,
    hasMandatoryAttributeGap: false,
    hasUomConflict: false,
    hasMpnFormatIssue: false,
    hasComplianceChange: false,
    hasSourceConflict: false,
    ...overrides,
});

export const DQ001_RUNTIME_SAMPLES = {
    passingRow: samplePart(),
    routedRow: samplePart({
        partId: "PART-DQ001-ROUTED",
        packageType: null,
        hasMandatoryAttributeGap: true,
    }),
};
